import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Code2, Download, LayoutDashboard, LogOut, User, ChevronDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const NAV = [
  { to: "/", label: "Beranda" },
  { to: "/products", label: "Katalog" },
  { to: "/products?type=SOURCE_CODE", label: "Source Code" },
  { to: "/categories", label: "Kategori" },
  { to: "/about", label: "Tentang" },
  { to: "/contact", label: "Kontak" },
];

export function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [menu, setMenu] = useState(false);
  const navigate = useNavigate();

  const dashPath = user?.role === "admin" ? "/admin/dashboard" : "/member/dashboard";

  const doLogout = async () => {
    await logout();
    setMenu(false);
    setOpen(false);
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-200/80">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-brand-logo">
            <span className="grid place-items-center h-9 w-9 rounded-lg bg-indigo-600 text-white">
              <Code2 className="h-5 w-5" />
            </span>
            <span className="font-heading font-extrabold text-lg tracking-tight text-slate-900">
              DS <span className="text-indigo-600">KODE</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => (
              <Link
                key={n.label}
                to={n.to}
                data-testid={`nav-link-${n.label.toLowerCase()}`}
                className="px-3 py-2 text-sm font-medium text-slate-600 hover:text-indigo-600 rounded-md transition-colors"
              >
                {n.label}
              </Link>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            {!user ? (
              <>
                <Link to="/login" data-testid="nav-login-btn" className="px-4 py-2 text-sm font-semibold text-slate-700 hover:text-indigo-600 transition-colors">
                  Masuk
                </Link>
                <Link to="/register" data-testid="nav-register-btn" className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg transition-colors">
                  Daftar Member
                </Link>
              </>
            ) : (
              <div className="relative">
                <button onClick={() => setMenu((m) => !m)} data-testid="nav-user-menu" className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                  <span className="grid place-items-center h-8 w-8 rounded-full bg-indigo-100 text-indigo-700 font-semibold text-sm overflow-hidden">
                    {user.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user.name?.[0] || "U").toUpperCase()}
                  </span>
                  <span className="text-sm font-semibold text-slate-700 max-w-[120px] truncate">{user.name}</span>
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                </button>
                {menu && (
                  <div className="absolute right-0 mt-2 w-52 bg-white border border-slate-200 rounded-xl shadow-lg py-1.5 animate-fade-up">
                    <MenuItem to={dashPath} icon={LayoutDashboard} label="Dashboard" onClick={() => setMenu(false)} testid="menu-dashboard" />
                    {user.role !== "admin" && <MenuItem to="/member/products" icon={Download} label="Produk Saya" onClick={() => setMenu(false)} testid="menu-my-products" />}
                    <MenuItem to={user.role === "admin" ? "/admin/profile" : "/member/profile"} icon={User} label="Profil" onClick={() => setMenu(false)} testid="menu-profile" />
                    <button onClick={doLogout} data-testid="menu-logout" className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors">
                      <LogOut className="h-4 w-4" /> Keluar
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <button onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle" className="lg:hidden p-2 rounded-md hover:bg-slate-100">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1 animate-fade-up">
          {NAV.map((n) => (
            <Link key={n.label} to={n.to} onClick={() => setOpen(false)} className="block px-3 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50 rounded-lg" data-testid={`mnav-${n.label.toLowerCase()}`}>
              {n.label}
            </Link>
          ))}
          <div className="pt-2 border-t border-slate-100 mt-2 space-y-2">
            {!user ? (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="block px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 rounded-lg">Masuk</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="block px-3 py-2.5 text-sm font-semibold text-white bg-indigo-600 rounded-lg text-center">Daftar Member</Link>
              </>
            ) : (
              <>
                <Link to={dashPath} onClick={() => setOpen(false)} className="block px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 rounded-lg">Dashboard</Link>
                <button onClick={doLogout} className="w-full text-left px-3 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-lg">Keluar</button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function MenuItem({ to, icon: Icon, label, onClick, testid }) {
  return (
    <Link to={to} onClick={onClick} data-testid={testid} className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
      <Icon className="h-4 w-4 text-slate-400" /> {label}
    </Link>
  );
}
